import { useState } from "react";
import { View, StyleSheet, ScrollView, Pressable, Linking } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import Modal from "@/components/Modal";
import { DangerousConfirmModal } from "@/components/Modals/DangerousConfirmModal";

interface InfoTabProps {
  shiftDescription?: string;
  projectDescription?: string;
  projectName?: string;
  dressCode?: {
    id: string;
    name: string;
    description?: string;
  };
  abilities?: { id: string; name: string }[];
  restrictions?: { id: string; name: string }[];
  // Onsite contact (supervisor-only)
  onsiteContact?: {
    name: string;
    phone_number?: string;
    email_address?: string;
  };
  isSupervisor?: boolean;
  // For navigation to contact detail
  shiftId?: string;
  // Decline shift action
  canDecline?: boolean;
  onDecline?: () => void;
  declining?: boolean;
}

export function InfoTab({
  shiftDescription,
  projectDescription,
  projectName,
  dressCode,
  abilities,
  restrictions,
  onsiteContact,
  isSupervisor,
  shiftId,
  canDecline,
  onDecline,
  declining,
}: InfoTabProps) {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const hasInfoContent =
    shiftDescription ||
    projectDescription ||
    dressCode ||
    (abilities && abilities.length > 0) ||
    (restrictions && restrictions.length > 0) ||
    (isSupervisor && onsiteContact);

  // Show empty state only if no info AND no decline action
  if (!hasInfoContent && !canDecline) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="information-outline" size={48} color={colors.placeholder} />
        <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No additional info</ThemedText>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Shift Description */}
      {shiftDescription && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="text" size={16} color={colors.text} />
            <ThemedText style={styles.sectionTitle}>{displayNames?.shifts?.name || "Shift"} Notes</ThemedText>
          </View>
          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>{shiftDescription}</ThemedText>
        </View>
      )}

      {/* Project Description */}
      {projectDescription && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="folder-outline" size={16} color={colors.text} />
            <ThemedText style={styles.sectionTitle}>
              {projectName || displayNames?.projects?.name || "Project"}
            </ThemedText>
          </View>
          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>{projectDescription}</ThemedText>
        </View>
      )}

      {/* Dress Code */}
      {/* {dressCode && ( */}
      {/*   <View style={styles.section}> */}
      {/*     <View style={styles.sectionHeader}> */}
      {/*       <MaterialCommunityIcons name="tshirt-crew" size={16} color={colors.text} /> */}
      {/*       <ThemedText style={styles.sectionTitle}>Dress Code</ThemedText> */}
      {/*     </View> */}
      {/*     <View style={[styles.dressCodeChip, { backgroundColor: "#8b5cf6", borderColor: "#7c3aed" }]}> */}
      {/*       <ThemedText style={styles.dressCodeText}>{dressCode.name}</ThemedText> */}
      {/*     </View> */}
      {/*     {dressCode.description && ( */}
      {/*       <ThemedText style={[styles.description, { color: colors.textSecondary, marginTop: 8 }]}> */}
      {/*         {dressCode.description} */}
      {/*       </ThemedText> */}
      {/*     )} */}
      {/*   </View> */}
      {/* )} */}
      {/* Onsite Contact - Supervisor Only */}
      {isSupervisor && onsiteContact && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="account-tie" size={16} color={colors.text} />
            <ThemedText style={styles.sectionTitle}>Onsite Contact</ThemedText>
          </View>
          <View
            style={[styles.contactCard, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          >
            <View style={styles.contactRow}>
              <Pressable
                onPress={() => {
                  if (!shiftId) return;
                  router.push(`home/${shiftId}/contact` as any);
                }}
                style={({ pressed }) => [styles.contactHeader, pressed && { opacity: 0.7 }]}
              >
                <Avatar
                  firstName={onsiteContact.name.split(" ")[0]}
                  lastName={onsiteContact.name.split(" ").slice(1).join(" ")}
                  size={36}
                />
                <ThemedText style={styles.contactName}>{onsiteContact.name}</ThemedText>
                <MaterialCommunityIcons name="chevron-right" size={18} color={colors.textSecondary} />
              </Pressable>
              {onsiteContact.phone_number && (
                <View style={styles.contactActions}>
                  <Pressable
                    onPress={() => Linking.openURL(`tel:${onsiteContact.phone_number}`)}
                    style={({ pressed, hovered }) => [
                      styles.contactActionButton,
                      { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                      hovered && { opacity: 0.8 },
                      pressed && { opacity: 0.6 },
                    ]}
                  >
                    <MaterialCommunityIcons name="phone" size={18} color={colors.emerald?.text} />
                  </Pressable>
                  <Pressable
                    onPress={() => Linking.openURL(`sms:${onsiteContact.phone_number}`)}
                    style={({ pressed, hovered }) => [
                      styles.contactActionButton,
                      { backgroundColor: colors.violet?.background, borderColor: colors.violet?.border },
                      hovered && { opacity: 0.8 },
                      pressed && { opacity: 0.6 },
                    ]}
                  >
                    <MaterialCommunityIcons name="message-text" size={18} color={colors.violet?.text} />
                  </Pressable>
                </View>
              )}
            </View>
          </View>
        </View>
      )}

      {/* Abilities */}
      {abilities && abilities.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons
              name="check-circle-outline"
              size={16}
              color={colors.emerald?.text || colors.primary}
            />
            <ThemedText style={styles.sectionTitle}>Required Abilities</ThemedText>
          </View>
          <View style={styles.tagsContainer}>
            {abilities.map((ability) => (
              <View
                key={ability.id}
                style={[
                  styles.tag,
                  { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                ]}
              >
                <ThemedText style={[styles.tagText, { color: colors.emerald?.text }]}>{ability.name}</ThemedText>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Restrictions */}
      {restrictions && restrictions.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.red.text} />
            <ThemedText style={styles.sectionTitle}>Restrictions</ThemedText>
          </View>
          <View style={styles.tagsContainer}>
            {restrictions.map((restriction) => (
              <View
                key={restriction.id}
                style={[styles.tag, { backgroundColor: colors.red?.background, borderColor: colors.red?.border }]}
              >
                <ThemedText style={[styles.tagText, { color: colors.red?.text }]}>{restriction.name}</ThemedText>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Danger Zone - Temporarily disabled */}
      {/* {canDecline && (
        <>
          {hasInfoContent && <View style={[styles.divider, { backgroundColor: colors.border }]} />}
          <View style={styles.dangerZone}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="alert-octagon" size={16} color={colors.red?.text} />
              <ThemedText style={[styles.sectionTitle, { color: colors.red?.text }]}>Danger Zone</ThemedText>
            </View>
            <Pressable
              onPress={() => setShowDeclineModal(true)}
              disabled={declining}
              style={({ pressed, hovered }) => [
                styles.declineButton,
                {
                  backgroundColor: colors.red?.background,
                  borderColor: colors.red?.border,
                  cursor: "pointer",
                } as any,
                hovered && { backgroundColor: colors.red?.border },
                pressed && { opacity: 0.7 },
                declining && { opacity: 0.5 },
              ]}
            >
              <MaterialCommunityIcons name="close" size={16} color={colors.red?.text} />
              <ThemedText style={[styles.declineText, { color: colors.red?.text }]}>Decline Shift</ThemedText>
            </Pressable>
          </View>
        </>
      )} */}

      {/* Decline Confirmation Modal - Temporarily disabled */}
      {/* {showDeclineModal && (
        <Modal onUnmount={() => setShowDeclineModal(false)}>
          <DangerousConfirmModal
            title="Decline Shift"
            message="Are you sure you want to decline this shift? This action cannot be undone and you may not be able to rejoin."
            confirmWord="decline"
            confirmText="Decline Shift"
            cancelText="Cancel"
            loading={declining}
            onConfirm={() => {
              onDecline?.();
            }}
            onCancel={() => setShowDeclineModal(false)}
          />
        </Modal>
      )} */}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 16,
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "600",
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
  },
  dressCodeChip: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  dressCodeText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#ffffff",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  tag: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
  },
  tagText: {
    fontSize: 12,
    fontWeight: "500",
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 4,
  },
  dangerZone: {
    gap: 12,
  },
  declineButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  declineText: {
    fontSize: 14,
    fontWeight: "600",
  },
  contactCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  contactHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  contactName: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },
  contactActions: {
    flexDirection: "row",
    gap: 8,
  },
  contactActionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
});
